import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  TrendingUp, TrendingDown, Users, Calendar, Wallet, 
  Clock, AlertCircle, CheckCircle, ArrowRight, X, Bell, 
  CreditCard, FileText, Shield, Trash2, CheckCheck, Filter
} from "lucide-react";
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, BarChart, Bar 
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(amount);
};

const StatCard = ({ title, value, icon: Icon, trend, color = "amber", onClick }) => {
  const colorClasses = {
    amber: "bg-amber-400/10 text-amber-400",
    emerald: "bg-emerald-400/10 text-emerald-400",
    rose: "bg-rose-400/10 text-rose-400",
    sky: "bg-sky-400/10 text-sky-400"
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      className={`bg-zinc-900 border border-zinc-800 p-6 rounded-sm ${onClick ? 'cursor-pointer hover:bg-zinc-800/50 transition-colors' : ''}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-zinc-500 text-xs tracking-wider uppercase">{title}</p>
          <p className="text-2xl font-outfit font-semibold text-zinc-50 mt-2">{value}</p>
          {trend && (
            <p className={`text-xs mt-2 ${trend > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {trend > 0 ? '+' : ''}{trend}% geçen aya göre
            </p>
          )}
        </div>
        <div className={`p-3 rounded-sm ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </motion.div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 p-3 rounded-sm">
        <p className="text-zinc-400 text-sm">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm font-mono" style={{ color: entry.color }}>
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reminders, setReminders] = useState(null);
  const [showRemindersPopup, setShowRemindersPopup] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [dismissedIds, setDismissedIds] = useState(new Set());

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard`, { withCredentials: true });
      setData(response.data);
    } catch (error) {
      // Silent error handling
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReminders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reminders`, { withCredentials: true });
      setReminders(response.data);
      // Auto show popup if there are critical/high priority reminders
      if (response.data.critical > 0 || response.data.high > 0) {
        setShowRemindersPopup(true);
      }
    } catch (error) {
      // Silent error handling
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchReminders();
  }, [fetchDashboard, fetchReminders]);

  const dismissReminder = async (reminderId) => {
    try {
      await axios.post(`${API}/reminders/dismiss/${reminderId}`, {}, { withCredentials: true });
      setDismissedIds(prev => new Set([...prev, reminderId]));
    } catch (error) {
      // Silent error handling
    }
  };

  const handleBulkAction = async (action, keepMonths = null) => {
    setBulkActionLoading(true);
    try {
      const response = await axios.post(`${API}/reminders/bulk-action`, {
        action: action,
        reminder_type: "all",
        keep_months: keepMonths
      }, { withCredentials: true });
      
      toast.success(response.data.message);
      setShowBulkActions(false);
      setShowRemindersPopup(false);
      // Refresh reminders
      fetchReminders();
    } catch (error) {
      toast.error("İşlem başarısız");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'border-rose-500 bg-rose-500/10';
      case 'high': return 'border-amber-500 bg-amber-500/10';
      default: return 'border-sky-500 bg-sky-500/10';
    }
  };

  const getPriorityIcon = (type) => {
    switch (type) {
      case 'payment': return CreditCard;
      case 'tax': return FileText;
      case 'insurance': return Shield;
      case 'bagkur': return Shield;
      default: return AlertCircle;
    }
  };

  const visibleReminders = reminders?.reminders?.filter(r => !dismissedIds.has(r.id)) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Reminders Popup */}
      <Dialog open={showRemindersPopup} onOpenChange={setShowRemindersPopup}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-zinc-50">
              <Bell className="w-5 h-5 text-amber-400" />
              Hatırlatmalar
              {reminders?.critical > 0 && (
                <span className="px-2 py-0.5 text-xs bg-rose-500/20 text-rose-400 rounded-full">
                  {reminders.critical} kritik
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">Ödeme hatırlatmaları listesi</DialogDescription>
          </DialogHeader>
          
          {/* Bulk Actions Toggle */}
          {visibleReminders.length > 5 && !showBulkActions && (
            <button
              onClick={() => setShowBulkActions(true)}
              className="flex items-center gap-2 text-sm text-zinc-400 hover:text-amber-400 transition-colors"
            >
              <Filter className="w-4 h-4" />
              Toplu İşlemler
            </button>
          )}
          
          {/* Bulk Actions Panel */}
          {showBulkActions && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="bg-zinc-950 border border-zinc-800 rounded-sm p-4 space-y-3"
            >
              <div className="flex items-center justify-between">
                <p className="text-sm text-zinc-400">Toplu İşlemler</p>
                <button onClick={() => setShowBulkActions(false)} className="text-zinc-500 hover:text-zinc-300">
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => handleBulkAction("keep_recent", 1)}
                  disabled={bulkActionLoading}
                  size="sm"
                  variant="outline"
                  className="border-zinc-700 text-xs"
                >
                  <CheckCheck className="w-3 h-3 mr-1" />
                  Son 1 ay koru
                </Button>
                <Button
                  onClick={() => handleBulkAction("keep_recent", 3)}
                  disabled={bulkActionLoading}
                  size="sm"
                  variant="outline"
                  className="border-zinc-700 text-xs"
                >
                  <CheckCheck className="w-3 h-3 mr-1" />
                  Son 3 ay koru
                </Button>
                <Button
                  onClick={() => handleBulkAction("mark_paid")}
                  disabled={bulkActionLoading}
                  size="sm"
                  className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 text-xs"
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Tümünü ödenmiş say
                </Button>
                <Button
                  onClick={() => {
                    if (window.confirm("Tüm ödenmemiş kayıtları silmek istediğinize emin misiniz? Bu işlem geri alınamaz.")) {
                      handleBulkAction("delete");
                    }
                  }}
                  disabled={bulkActionLoading}
                  size="sm"
                  className="bg-rose-500/20 text-rose-400 hover:bg-rose-500/30 text-xs"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Tümünü sil
                </Button>
              </div>
              
              <p className="text-xs text-zinc-500">
                "Son X ay koru" seçeneği, seçilen tarihten önceki kayıtları "ödendi" olarak işaretler.
              </p>
            </motion.div>
          )}
          
          <div className="max-h-[50vh] overflow-y-auto space-y-2 pr-2">
            <AnimatePresence>
              {visibleReminders.length > 0 ? (
                visibleReminders.map((reminder, index) => {
                  const Icon = getPriorityIcon(reminder.type);
                  return (
                    <motion.div
                      key={reminder.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ delay: index * 0.05 }}
                      className={`relative p-4 border-l-4 rounded-sm ${getPriorityColor(reminder.priority)}`}
                    >
                      <button
                        onClick={() => dismissReminder(reminder.id)}
                        className="absolute top-2 right-2 text-zinc-500 hover:text-zinc-300"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <div className="flex items-start gap-3">
                        <Icon className={`w-5 h-5 mt-0.5 ${
                          reminder.priority === 'critical' ? 'text-rose-400' :
                          reminder.priority === 'high' ? 'text-amber-400' : 'text-sky-400'
                        }`} />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-zinc-50">{reminder.title}</p>
                          <p className="text-xs text-zinc-400 mt-0.5">{reminder.description}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-sm font-mono text-zinc-300">
                              {formatCurrency(reminder.amount)}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              reminder.overdue 
                                ? 'bg-rose-500/20 text-rose-400' 
                                : 'bg-zinc-800 text-zinc-400'
                            }`}>
                              {reminder.overdue 
                                ? `${Math.abs(reminder.days_until)} gün gecikti`
                                : reminder.days_until === 0 
                                  ? 'Bugün!'
                                  : `${reminder.days_until} gün kaldı`
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="w-10 h-10 mx-auto text-emerald-400 mb-2" />
                  <p className="text-zinc-400">Tüm hatırlatmalar tamamlandı!</p>
                </div>
              )}
            </AnimatePresence>
          </div>
          {visibleReminders.length > 0 && (
            <div className="pt-4 border-t border-zinc-800">
              <Button 
                onClick={() => setShowRemindersPopup(false)}
                className="w-full bg-amber-400 hover:bg-amber-500 text-zinc-900"
              >
                Anladım
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Page Title */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-zinc-50">Dashboard</h1>
          <p className="text-zinc-500 text-sm mt-1">Finansal özet ve güncel durum</p>
        </div>
        {/* Reminders Bell */}
        {reminders && reminders.total > 0 && (
          <button
            onClick={() => setShowRemindersPopup(true)}
            className="relative p-2 bg-zinc-900 border border-zinc-800 rounded-sm hover:border-amber-400/50 transition-colors"
            data-testid="reminders-bell"
          >
            <Bell className="w-5 h-5 text-zinc-400" />
            <span className={`absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs rounded-full ${
              reminders.critical > 0 ? 'bg-rose-500' : 'bg-amber-400'
            } text-zinc-900 font-medium`}>
              {reminders.total}
            </span>
          </button>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Toplam Alacak" 
          value={formatCurrency(data?.total_receivable || 0)} 
          icon={TrendingUp}
          color="emerald"
          onClick={() => navigate('/customers?filter=receivable')}
        />
        <StatCard 
          title="Toplam Borç" 
          value={formatCurrency(data?.total_payable || 0)} 
          icon={TrendingDown}
          color="rose"
        />
        <StatCard 
          title="Net Bakiye" 
          value={formatCurrency(data?.net_balance || 0)} 
          icon={Wallet}
          color={data?.net_balance >= 0 ? "emerald" : "rose"}
        />
        <StatCard 
          title="Personel Sayısı" 
          value={data?.total_employees || 0} 
          icon={Users}
          color="sky"
        />
      </div>

      {/* Second Row Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Bekleyen İzinler" 
          value={data?.pending_leaves || 0} 
          icon={Calendar}
          color="amber"
        />
        <StatCard 
          title="Ödenmemiş Maaş" 
          value={data?.unpaid_salaries || 0} 
          icon={Clock}
          color={data?.unpaid_salaries > 0 ? "rose" : "emerald"}
        />
        <StatCard 
          title="Vergi Borcu" 
          value={formatCurrency(data?.total_tax_due || 0)} 
          icon={AlertCircle}
          color={data?.total_tax_due > 0 ? "rose" : "emerald"}
        />
        <StatCard 
          title="Sigorta Borcu" 
          value={formatCurrency(data?.total_insurance_due || 0)} 
          icon={AlertCircle}
          color={data?.total_insurance_due > 0 ? "rose" : "emerald"}
        />
      </div>

      {/* Charts and Upcoming */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Chart */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-2 bg-zinc-900 border border-zinc-800 p-6 rounded-sm"
        >
          <h3 className="text-lg font-outfit font-medium text-zinc-50 mb-4">Aylık Gelir/Gider</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.monthly_chart || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
                <YAxis stroke="#71717a" fontSize={12} tickFormatter={(value) => `₺${value/1000}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="income" name="Gelir" fill="#34d399" radius={[2, 2, 0, 0]} />
                <Bar dataKey="expense" name="Gider" fill="#f87171" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Upcoming Payments */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-zinc-900 border border-zinc-800 p-6 rounded-sm"
        >
          <h3 className="text-lg font-outfit font-medium text-zinc-50 mb-4">Yaklaşan Ödemeler</h3>
          <div className="space-y-3">
            {data?.upcoming_payments?.length > 0 ? (
              data.upcoming_payments.map((payment, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-3 bg-zinc-950 border border-zinc-800 rounded-sm"
                >
                  <div>
                    <p className="text-sm text-zinc-50">{payment.description}</p>
                    <p className="text-xs text-zinc-500">{payment.due_date}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-mono ${payment.type === 'receivable' ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {payment.type === 'receivable' ? '+' : '-'}{formatCurrency(payment.amount)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-zinc-500">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                <p className="text-sm">Yaklaşan ödeme yok</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
